import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, Modal, StyleSheet } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { ToggleButton } from './ToggleButton';
import { BottomBar } from './BottomBar';
import { TapToTalk } from './TapToTalk';
import { AutoDetect } from './AutoDetect';
import { useTutor } from '../../contexts/TutorContext';
import { AvatarContainer } from './AvatarContainer';
import type { UserInfo } from '../../types';

interface MainProps {
  userInfo: UserInfo;
  onEndCall: () => void;
  sessionId: string;
}

export const Main: React.FC<MainProps> = ({
  userInfo,
  onEndCall,
  sessionId,
}) => {
  const { selectedTutor } = useTutor();
  const [isAutoMode, setIsAutoMode] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [response, setResponse] = useState('');
  const [error, setError] = useState('');
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (selectedTutor) {
      setIsReady(true);
      setError('');
    } else {
      setIsReady(false);
      setResponse('');
      setError('');
    }
  }, [selectedTutor]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    timer = setInterval(() => {
      setDuration(prev => prev + 1);
    }, 1000);
    return () => {
      if (timer) clearInterval(timer);
    };
  }, []);

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleResponse = (text: string) => {
    setResponse(text);
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
  };

  const handleAutoModeToggle = (autoMode: boolean) => {
    setIsAutoMode(autoMode);
    setShowMessages(autoMode);
  };

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: '#1a1a1a',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      height: '100vh',
      overflow: 'hidden',
    },
    header: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 5,
      paddingHorizontal: 5,
      flexShrink: 0,
      '@media (max-width: 768px)': {
        paddingVertical: 8,
        paddingHorizontal: 4,
      },
    },
    durationText: {
      color: 'rgba(255, 255, 255, 0.6)',
      fontSize: 16,
      marginBottom: 16,
    },
    autoDetectContainer: {
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    audioCallContainer: {
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    closeButton: {
      position: 'absolute',
      right: 16,
      top: 16,
      padding: 8,
      zIndex: 10,
    },
  });

  return (
    <Modal
      animationType="slide"
      transparent={false}
      visible={true}
      onRequestClose={onEndCall}
    >
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={styles.tutorSection}>
            <AvatarContainer duration={duration} />
            <ToggleButton 
              isAutoMode={isAutoMode}
              onToggle={handleAutoModeToggle}
            />
          </View>
          <TouchableOpacity style={styles.closeButton} onPress={onEndCall}>
            <MaterialIcons name="close" size={24} color="#fff" />
          </TouchableOpacity>
        </View>
        
        <View style={styles.contentArea}> 
          <View style={styles.messagesContainer}>
            {isAutoMode ? (
              <View style={styles.autoDetectContainer}>
                <AutoDetect
                  sessionId={sessionId}
                  tutorId={selectedTutor?.id || ''}
                  onResponse={handleResponse}
                  onError={handleError}
                  isReady={isReady}
                  showMessages={showMessages}
                />
              </View>
            ) : (
              <View style={styles.audioCallContainer}>
                <TapToTalk
                  sessionId={sessionId}
                  tutorId={selectedTutor?.id || ''}
                  onResponse={handleResponse}
                  onError={handleError}
                  isReady={isReady}
                />
              </View>
            )}
          </View>
          <BottomBar onEndCall={onEndCall} />
        </View>
      </View>
    </Modal>
  );
};
